export function getYearMonthDay(dateInput) {
  let year;
  let month;
  let day;
  let hours = '00';
  let minutes = '00';
  if (dateInput instanceof Date) {
    year = dateInput.getFullYear();
    month = (dateInput.getMonth() + 1).toString().padStart(2, '0');
    day = dateInput.getDate().toString().padStart(2, '0');
    hours = dateInput.getHours().toString().padStart(2, '0');
    minutes = dateInput.getMinutes().toString().padStart(2, '0');
  } else if (typeof dateInput === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(dateInput)) {
      // ISO date string like 2025-04-01T07:00:00.000Z
      const date = new Date(dateInput);
      year = date.getUTCFullYear();
      month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      day = date.getUTCDate().toString().padStart(2, '0');
      hours = date.getUTCHours().toString().padStart(2, '0');
      minutes = date.getUTCMinutes().toString().padStart(2, '0');
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateInput)) {
      // Format: MM/DD/YYYY (no time)
      const [mm, dd, yyyy] = dateInput.split('/');
      year = yyyy;
      month = mm.padStart(2, '0');
      day = dd.padStart(2, '0');
    } else {
      throw new Error('Unsupported date format string');
    }
  } else {
    throw new Error('Input must be a Date object or a supported date string');
  }

  return { year, month, day, hours, minutes };
}

// converts from mm/dd/yyyy -> yyyy-mm-dd
export function convertDateFormatToHtmlDate(dateStr) {
  const { year, month, day } = getYearMonthDay(dateStr);
  return `${year}-${month}-${day}`;
}

// converts from 2025-04-01T07:00:00.000Z -> HH:MM
export function formatDateTimetoHtmlTime(datetimeStr) {
  const { hours, minutes } = getYearMonthDay(datetimeStr);
  return `${hours}:${minutes}`;
}

// Convert from  2025-04-01T07:00:00.000Z -> 2025-04-01 (yyyy-mm-dd)
export function formatDateTimeToHtmlDate(datetimeStr) {
  const { year, month, day } = getYearMonthDay(datetimeStr);
  return `${year}-${month}-${day}`;
}

// Convert from dd/mm/yyyy to yyyy-mm-dd
export function convertDdMmYyyyToHtmlDate(dateStr) {
  const [day, month, year] = dateStr.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

// Convert Jul-2023 -> 2023-07-01
export function convertMonthYearToHtmlDate(monthYearStr) {
  const [monthStr, year] = monthYearStr.split('-');
  const monthIndex = new Date(Date.parse(`${monthStr} 1, 2023`)).getMonth();
  const month = (monthIndex + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
}

// Format Date Object to date string (yyyy-mm-dd)
export function formatDateObjToHtmlDate(date) {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateObjToHtmlDateTime(date) {
  const { year, month, day, hours, minutes } = getYearMonthDay(date);
  return `${year}-${month}-${day}T${hours}:${minutes}:00`;
}

// Get current full month data from a Date
export const getMonthData = date => {
  const year = date.getFullYear();
  const monthIndex = date.getMonth();

  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);

  return {
    fromDate: firstDay.toISOString(),
    toDate: lastDay.toISOString(),
    month: firstDay.toLocaleString('default', { month: 'short' }), // e.g., "Apr"
    year,
    baseDate: firstDay, // keep internally for calculating next/prev
  };
};

// 9:34:19 AM  -> 09:34
// 7:19:06 PM  -> 19:19
// Convert 12-hour format to 24-hour format
export const convertTimeToHTMLTime = time => {
  const [timeStr, modifier] = time.split(' ');
  const timeData = timeStr.split(':');
  const hours = timeData[0];
  const minutes = timeData[1];
  let transformHours = hours;
  if (modifier === 'PM' && hours !== '12') {
    transformHours = parseInt(hours, 10) + 12;
  } else if (modifier === 'AM' && hours === '12') {
    transformHours = '00';
  }
  return `${transformHours < 10 ? `0${transformHours}` : transformHours}:${minutes}`;
};

export const convertTimeToHTMLTimeRegex = time => {
  // matches “H:MMAM”, “HH:MM PM”, etc.
  const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
  const match = time.match(timeRegex);
  if (!match) {
    throw new Error('Invalid time format');
  }
  const [, hoursMatch, minutes, modifier] = match;
  let hours = hoursMatch;
  hours = parseInt(hours, 10);
  const minutesConst = minutes;

  if (modifier.toUpperCase() === 'PM' && hours !== 12) {
    hours += 12;
  } else if (modifier.toUpperCase() === 'AM' && hours === 12) {
    hours = 0;
  }
  // pad to 2 digits
  const hh = hours < 10 ? `0${hours}` : `${hours}`;
  return `${hh}:${minutesConst}`;
};

/**
 * Returns the start and end datetime of a given year,
 * both at 00:00:00.
 * @param {number} year - e.g. 2025
 * @returns {{ startDate: string, endDate: string }}
 */
export function getYearRange(year) {
  const pad = n => String(n).padStart(2, '0');

  // start: Jan 1st at 00:00:00
  const start = new Date(year, 0, 1, 0, 0, 0);

  // end: Dec 31st at 00:00:00
  const end = new Date(year, 11, 31, 0, 0, 0);

  const fmt = dt =>
    `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}` +
    ` ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;

  return {
    startDate: fmt(start),
    endDate: fmt(end),
  };
}

/**
 * Returns the start and end datetime of a given month in a given year,
 * both at 00:00:00.
 * @param {number} monthIndex - 0 for January through 11 for December
 * @param {number} year
 * @returns {{ startDate: string, endDate: string }}
 */
export function getMonthRange(monthIndex, year) {
  const pad = n => String(n).padStart(2, '0');
  const m = monthIndex; // now directly 0–11

  // start: 1st day of month at 00:00:00
  const start = new Date(year, m, 1, 0, 0, 0);

  // end: last day of month at 00:00:00
  // (day 0 of next month is last day of current)
  const end = new Date(year, m + 1, 0, 0, 0, 0);
  const fmt = dt =>
    `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}` +
    ` ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;

  return {
    startDate: fmt(start),
    endDate: fmt(end),
  };
}

function formatTime(date) {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // convert to 12-hour, treating 0 as 12
  const mins = String(minutes).padStart(2, '0');
  return `${hours}:${mins} ${ampm}`;
}

function getMonthName(date) {
  // short English month names
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return months[date.getMonth()];
}

// GIven a date string, convert it to a chat-friendly datetime format
// 2025-05-30T01:06:24.956Z
// COnvert to 21 Mar 2025, 10:00 AM or Just now
export function convertToChatDateTime(dateTimeStr) {
  const date = new Date(dateTimeStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 60) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }
  return `${date.getDate()} ${getMonthName(date)} ${date.getFullYear()}, ${formatTime(date)}`;
}

/**
 * Given an elapsed time in seconds, returns:
 *  - “Just now” for < 60s
 *  - “X minutes ago” for < 1h
 *  - “X hours ago” for < 24h
 *  - “X days ago” otherwise
 */
export function formatElapsedSeconds(elapsedSeconds) {
  if (elapsedSeconds < 60) {
    return 'Just now';
  }

  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

export const getCurrentQuarter = () => {
  const month = new Date().getMonth(); // 0-based (Jan = 0, Dec = 11)

  if (month >= 3 && month <= 5) return 1; // Apr - Jun
  if (month >= 6 && month <= 8) return 2; // Jul - Sep
  if (month >= 9 && month <= 11) return 3; // Oct - Dec
  return 4; // Jan - Mar
};

/**
 * Converts an ISO-8601 timestamp into "DD/MM/YYYY HH:mm:ss" by slicing the string.
 * @param {string} iso – e.g. "2018-06-11T08:41:29.683Z"
 * @returns {string} formatted date-time, e.g. "11/06/2018 08:41:29"
 */
export function isoToDdMmYyyyHhMmSsSlice(iso) {
  if (!iso) {
    return null;
  }
  // YYYY-MM-DDTHH:mm:ss.sssZ
  const year = iso.slice(0, 4);
  const month = iso.slice(5, 7);
  const day = iso.slice(8, 10);
  const hour = iso.slice(11, 13);
  const minute = iso.slice(14, 16);
  const second = iso.slice(17, 19);

  return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
}

/**
 * @param {string} iso – e.g. "2018-06-09T08:41:29.683Z"
 * @returns {string} "09/06/2018"
 */
export function isoToDdMmYyyySlice(iso) {
  if (!iso) {
    return null;
  }
  // characters 8–9 = day, 5–6 = month, 0–3 = year
  const day = iso.slice(8, 10);
  const month = iso.slice(5, 7);
  const year = iso.slice(0, 4);
  return `${day}/${month}/${year}`;
}

export function getCurrentFinancialPeriod() {
  const currentYear = new Date().getFullYear();
  return `${currentYear}-${currentYear + 1}`;
}

export function makeTime(h, m = 0) {
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

export function formatISOToDateAndTime(iso) {
  if (!iso) return '';
  const [datePart, timePart] = iso.split('T');

  const [year, month, day] = datePart.split('-');
  const [hour, minute, secondWithMs] = timePart.replace('Z', '').split(':');

  const second = secondWithMs.split('.')[0]; // remove milliseconds

  return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
}
